import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, EmptyState, StatusPill, Avatar, ProgressBar } from "../../components/Atoms";
import { TrendChart, CategoryPie } from "../../components/Charts";
import { ChartLineUp, FileXls, FileCsv, ListChecks, CurrencyDollarSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReportsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, p] = await Promise.all([api.get("/analytics/admin"), api.get("/projects")]);
      setAnalytics(a.data); setProjects(p.data);
    })();
  }, []);

  const dl = async (endpoint, name) => {
    setBusy(endpoint);
    try { await downloadFile(endpoint, name); toast.success(`${name} downloaded`); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const openProgress = async (pid) => {
    try { const { data } = await api.get(`/reports/project/${pid}/progress`); setProgress(data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Reports & Analytics" subtitle="Excel exports + creator-level progress" />

      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={() => dl("/reports/progress/xlsx", "progress-report.xlsx")}
          disabled={busy === "/reports/progress/xlsx"}
          data-testid="admin-dl-progress-xlsx"
          className="glass p-5 hover-lift text-left flex items-center gap-4 disabled:opacity-50">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center glow-cyan">
            <ListChecks size={22} weight="fill" color="white" />
          </div>
          <div className="flex-1">
            <div className="text-white font-bold">Complete Progress (.xlsx)</div>
            <div className="text-xs text-muted">Overview + task × creator matrix for every project</div>
          </div>
          <FileXls size={20} className="text-[#A3E635]" weight="bold" />
        </button>
        <button onClick={() => dl("/reports/payouts/xlsx", "payouts-report.xlsx")}
          disabled={busy === "/reports/payouts/xlsx"}
          data-testid="admin-dl-payouts-xlsx"
          className="glass p-5 hover-lift text-left flex items-center gap-4 disabled:opacity-50">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#A3E635] grid place-items-center glow-lime">
            <CurrencyDollarSimple size={22} weight="fill" color="white" />
          </div>
          <div className="flex-1">
            <div className="text-white font-bold">Payouts Report (.xlsx)</div>
            <div className="text-xs text-muted">All payouts, statuses, notes & totals</div>
          </div>
          <FileXls size={20} className="text-[#A3E635]" weight="bold" />
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="glass p-5 lg:col-span-2">
          <div className="text-white font-bold mb-1">Trends</div><div className="text-xs text-muted mb-3">Payouts vs Submissions</div>
          {analytics ? <TrendChart data={analytics.series} /> : <div className="skeleton h-60" />}
        </div>
        <div className="glass p-5">
          <div className="text-white font-bold mb-1">Categories</div>
          {analytics ? <CategoryPie data={analytics.categories} /> : <div className="skeleton h-60" />}
        </div>
      </div>

      <div className="glass p-5">
        <div className="text-white font-bold mb-3">Per-Project</div>
        {projects.length === 0 ? <EmptyState icon={ChartLineUp} /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map(p => (
              <div key={p.id} className="p-4 rounded-2xl bg-white/3 border border-white/5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{p.title}</div>
                    <div className="text-xs text-muted truncate">{p.client_name}</div>
                  </div>
                  <StatusPill status={p.status} />
                </div>
                <div className="text-xs text-muted mt-2">{p.task_count} tasks · {fmtMoney(p.reward_amount)}</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => openProgress(p.id)} data-testid={`admin-task-progress-${p.id}`}
                    className="btn-ghost text-xs flex items-center gap-1.5"><ListChecks size={14} weight="bold" /> Task progress</button>
                  <button onClick={() => dl(`/reports/project/${p.id}/csv`, `project-${p.title}.csv`)}
                    data-testid={`admin-dl-project-csv-${p.id}`}
                    className="btn-ghost text-xs flex items-center gap-1.5 text-[#22D3EE]"><FileCsv size={14} weight="bold" /> .csv</button>
                  <button onClick={() => dl(`/reports/project/${p.id}/xlsx`, `project-${p.title}.xlsx`)}
                    data-testid={`admin-dl-project-xlsx-${p.id}`}
                    className="btn-ghost text-xs flex items-center gap-1.5 text-[#A3E635]"><FileXls size={14} weight="bold" /> .xlsx</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {progress && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setProgress(null)}>
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
          <div className="glass relative p-5 sm:p-6 w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-white text-xl font-bold">{progress.project.title}</div>
                <div className="text-xs text-muted">Per creator · per task status matrix</div>
              </div>
              <button onClick={() => dl(`/reports/project/${progress.project.id}/xlsx`, `project-${progress.project.title}.xlsx`)}
                className="btn-primary flex items-center gap-2 text-sm"><FileXls size={14} weight="bold" /> Export .xlsx</button>
            </div>
            {progress.rows.length === 0 ? <EmptyState title="No approved creators yet" /> : (
              <div className="space-y-3">
                {progress.rows.map(r => (
                  <div key={r.ambassador_id} className="p-4 rounded-2xl bg-white/3 border border-white/5">
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar src={r.ambassador_photo} name={r.ambassador_name} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold truncate">{r.ambassador_name}</div>
                        <div className="text-xs text-muted">{r.city || "—"} · {r.completed}/{progress.tasks.length} done</div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-black">{r.progress_pct}%</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted font-bold">progress</div>
                      </div>
                    </div>
                    <ProgressBar value={r.completed} max={progress.tasks.length || 1} color="#A3E635" />
                    <div className="grid sm:grid-cols-2 gap-2 mt-3">
                      {r.tasks.map(t => (
                        <div key={t.task_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/5">
                          <div className="text-sm text-white truncate flex-1">{t.task_title}</div>
                          <StatusPill status={t.status} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setProgress(null)} className="btn-ghost w-full mt-4">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
